import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _debounce from 'lodash/debounce';
import {
  AiConversationFilterInputObject,
  SortDirectionEnum,
  useAiConversationDestroyMutation,
  useAiConversationsQuery,
  useAiConversationUpdateMutation,
} from 'graphql-common';
import { logger } from '@lib/services/logger';
import toast from '@lib/components/Toast/Toast';

const CONVERSATIONS_LIMIT = 100;
const SEARCH_DEBOUNCE_MS = 500;

type Args = {
  skip?: boolean;
  onDestroyed?: (id: string) => void;
};

export default function useAiConversations({ skip, onDestroyed }: Args = {}) {
  const { t } = useTranslation();

  // `search` drives the (controlled) input and empty-state UI immediately;
  // `debouncedSearch` drives the query filter so we don't refetch on every
  // keystroke (mirrors lib Search.tsx).
  const [search, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const debouncedSetSearch = useRef(
    _debounce((value: string) => setDebouncedSearch(value), SEARCH_DEBOUNCE_MS),
  ).current;

  // Cancel any pending debounced update when the hook unmounts (modal close).
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const setSearch = (value: string) => {
    setSearchValue(value);
    debouncedSetSearch(value);
  };

  const filters: AiConversationFilterInputObject[] = debouncedSearch
    ? [{ title: { predicate: { cont: debouncedSearch } } }]
    : [];

  const conversationsQuery = useAiConversationsQuery({
    variables: {
      page: 1,
      limit: CONVERSATIONS_LIMIT,
      sorts: { updatedAt: SortDirectionEnum.Desc },
      filters,
    },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const [updateMutation, updateMutationResult] =
    useAiConversationUpdateMutation();
  const [destroyMutation] = useAiConversationDestroyMutation();

  // Tracks the conversation currently being destroyed so the sidebar can
  // close/hide its actions menu right away instead of leaving it open for
  // the duration of the mutation + refetch.
  const [destroyingId, setDestroyingId] = useState<string | null>(null);

  const refetch = () => {
    logger.debug('AiAssistant: refetching conversations list', { search });
    return conversationsQuery.refetch();
  };

  // Rejects on failure so the caller (rename modal) keeps the dialog open and
  // surfaces the error; the Apollo error is also exposed via `renameError`.
  const renameConversation = (id: string, title: string) => {
    logger.debug('AiAssistant: renaming conversation', {
      conversationId: id,
      titleLength: title.length,
    });
    return updateMutation({
      variables: { id, attributes: { title } },
    })
      .then(() => refetch())
      .catch((error) => {
        logger.error('AiAssistant: rename conversation failed', {
          conversationId: id,
          error,
        });
        throw error;
      });
  };

  const destroyConversation = (id: string) => {
    logger.debug('AiAssistant: destroying conversation', {
      conversationId: id,
    });
    setDestroyingId(id);
    return destroyMutation({ variables: { id } })
      .then(() => {
        onDestroyed?.(id);
        toast({ content: t('ai-chat-deleted') });
        return refetch();
      })
      .catch((error) => {
        logger.error('AiAssistant: destroy conversation failed', {
          conversationId: id,
          error,
        });
        toast({ content: error.message || t('something-went-wrong') });
      })
      .finally(() => setDestroyingId(null));
  };

  return {
    conversations: conversationsQuery.data?.data.collection ?? [],
    loading: conversationsQuery.loading,
    search,
    setSearch,
    refetch,
    renameConversation,
    renameLoading: updateMutationResult.loading,
    renameError: updateMutationResult.error,
    destroyConversation,
    destroyingId,
  };
}
